import { groupBy, keyBy, minBy } from "lodash";
import { Mention } from "~/sharedComponents/advancedEditor/types/types";
import { suggestionsFilter } from "~/sharedComponents/advancedEditor/utils/suggestionsFilter";
import { groupKey, resourceGroups } from "~/sharedComponents/select/multiSelect/userSelect/UserGroups";
import { MentionSuggestionItem } from "../types";

const getUniqueKey = ({ id, teamId }: MentionSuggestionItem) => {
    if (teamId) {
        return `T_${teamId}`;
    }

    return `U_${id}`;
};

export const suggestionsFilterWithMembers = ({
    searchValue,
    suggestions,
    projectMembers,
    spaceMembers,
    isSpacesEnabled,
}: {
    searchValue: string;
    suggestions: Mention[];
    projectMembers?: number[];
    spaceMembers?: number[];
    isSpacesEnabled?: boolean;
}) => {
    const filteredBySearchSuggestions = suggestionsFilter(searchValue, suggestions) as MentionSuggestionItem[];
    const groupedByName = groupBy(filteredBySearchSuggestions, fs => fs.name.toLowerCase());

    const uniqueIdByName: Dictionary<string> = {};
    for (let [key, mentions] of Object.entries(groupedByName)) {
        const min = minBy(mentions, x => x.id) ?? minBy(mentions, x => x.teamId);

        uniqueIdByName[key] = getUniqueKey(min);
    }

    const filteredSuggestions = filteredBySearchSuggestions.filter(
        mention => uniqueIdByName[mention.name.toLowerCase()] === getUniqueKey(mention)
    );

    const defaultGroup = (projectMembers || []).length === 0 ? groupKey.users : groupKey.nonProjectMembers;
    const projectMembersHash = keyBy(projectMembers, x => x);
    const spaceMembersHash = spaceMembers?.length ? keyBy(spaceMembers, x => x) : null;

    const getGroup = ({ id, teamId }: MentionSuggestionItem) => {
        if (teamId != null) {
            return groupKey.teams;
        }

        if (projectMembersHash[id]) {
            return groupKey.projectMembers;
        }

        if (isSpacesEnabled && spaceMembersHash) {
            return spaceMembersHash[id] ? groupKey.spaceMembers : groupKey.nonSpaceMembers;
        }

        return defaultGroup;
    };

    const groups = groupBy(filteredSuggestions, getGroup);

    return [
        groupKey.teams,
        groupKey.projectMembers,
        groupKey.nonProjectMembers,
        groupKey.users,
        groupKey.spaceMembers,
        groupKey.nonSpaceMembers,
    ].flatMap(groupKey => {
        const group = groups[groupKey] ?? [];

        if (group.length) {
            group[0] = { ...group[0], group: resourceGroups[groupKey] };
        }

        return group;
    });
};
